import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiRequest } from "../../../../../shared/api/client";
import type { SessionUser } from "../../../../../shared/auth/access";
import type { RequestDetailData, RequestOfferEvaluation } from "../../../domain/request-detail-types";
import { useRequestDetailScope } from "./useRequestDetailScope";

type State = {scope: ReturnType<typeof useRequestDetailScope>; detail: RequestDetailData | null; evaluation: RequestOfferEvaluation | null; loading: boolean};
export function useRequestDetailData({requestNo, user, notify}: {requestNo: string; user: SessionUser; notify: (message: string) => void}) {
  const scope = useRequestDetailScope(requestNo, user, notify);
  const [state, setState] = useState<State>({scope, detail: null, evaluation: null, loading: true});
  const versions = useRef({load: 0, detail: 0, evaluation: 0});
  const setDetail: Dispatch<SetStateAction<RequestDetailData | null>> = useCallback(value => {
    if (!scope.isCurrent() || typeof value !== "function" && value && value.id !== requestNo) return;
    versions.current.detail++;
    setState(current => {
      const previous = current.scope === scope ? current : {scope, detail: null, evaluation: null, loading: true};
      const next = typeof value === "function" ? value(previous.detail) : value;
      return next && next.id !== requestNo ? previous : {...previous, detail: next};
    });
  }, [scope, requestNo]);
  const setEvaluation: Dispatch<SetStateAction<RequestOfferEvaluation | null>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    versions.current.evaluation++;
    setState(current => {
      const previous = current.scope === scope ? current : {scope, detail: null, evaluation: null, loading: true};
      return {...previous, evaluation: typeof value === "function" ? value(previous.evaluation) : value};
    });
  }, [scope]);
  const reload = useCallback(async () => {
    if (!scope.isCurrent()) return;
    const snapshot = {...versions.current, load: ++versions.current.load};
    setState(current => current.scope === scope ? {...current, loading: true} : {scope, detail: null, evaluation: null, loading: true});
    const isCurrent = () => scope.isCurrent() && snapshot.load === versions.current.load;
    try {
      const [detail, evaluation] = await Promise.all([
        apiRequest<RequestDetailData>(`/api/requests/${encodeURIComponent(requestNo)}`),
        apiRequest<RequestOfferEvaluation>(`/api/requests/${encodeURIComponent(requestNo)}/evaluations/latest`).catch(() => null),
      ]);
      if (!isCurrent()) return;
      if (!detail || detail.id !== requestNo) throw new Error("Talep yanıtı seçilen kayıtla uyuşmuyor.");
      setState(current => ({scope,
        detail: snapshot.detail === versions.current.detail ? detail : current.scope === scope ? current.detail : null,
        evaluation: snapshot.evaluation === versions.current.evaluation ? evaluation : current.scope === scope ? current.evaluation : null,
        loading: false,
      }));
    } catch (error) {
      if (isCurrent()) scope.notify(error instanceof Error ? error.message : "Talep detayı alınamadı.");
    } finally {
      if (isCurrent()) setState(current => ({...current, scope, loading: false}));
    }
  }, [scope, requestNo]);
  useEffect(() => {void reload();}, [reload]);
  const visible = state.scope === scope;
  return {scope, detail: visible ? state.detail : null, evaluation: visible ? state.evaluation : null, loading: !visible || state.loading, setDetail, setEvaluation, reload};
}
